import { Tooltip } from '@heroui/react'
import { useQuery } from '@tanstack/react-query'
import { Lock, Play } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from 'hooks/useAuth'


export default function LiveListen({ callId, isCallEnded }) {
  const [isPlaying, setIsPlaying] = useState(false)
  const navigate = useNavigate()
  const { org } = useAuth();

  function connectWebSocket(callId) {
    const socket = new WebSocket(`wss://api.bland.ai/v1/livelisten/calls/${callId}`)
    socket.binaryType = 'arraybuffer'
    

    const audioContext = new (window.AudioContext || window.webkitAudioContext)()
    const audioQueue = []
    let isPlaying = false
    let startTime = null

    function playNextInQueue() {
      if (audioQueue.length > 0 && !isPlaying) {
        isPlaying = true
        const buffer = audioQueue.shift()
        const source = audioContext.createBufferSource()
        source.buffer = buffer
        source.connect(audioContext.destination)
        source.onended = () => {
          isPlaying = false
          playNextInQueue()
        }

        if (startTime === null) {
          startTime = audioContext.currentTime
        }

        source.start(startTime)
        startTime += buffer.duration
      }
    }

    socket.onmessage = (event) => {
      const data = new DataView(event.data)
      const int16 = new Int16Array(data.byteLength / 2)

      for (let i = 0; i < int16.length; i++) {
        int16[i] = data.getInt16(i * 2, true)
      }

      const float32 = new Float32Array(int16.length)
      for (let i = 0; i < int16.length; i++) {
        float32[i] = Math.max(-1, Math.min(1, int16[i] / 32768))
      }

      try {
        const buffer = audioContext.createBuffer(1, float32.length, 16000)
        buffer.copyToChannel(float32, 0)

        audioQueue.push(buffer)

        if (!isPlaying && audioQueue.length > 3) {
          playNextInQueue()
        }
      }
      catch (e) {
        console.error('Buffer creation or copying failed:', e)
      }
    }

    socket.onclose = () => {
      isPlaying = false
    }

    return socket
  }

  useEffect(() => {
    let ws

    if (isPlaying) {
      ws = connectWebSocket(callId)
    }

    return () => {
      if (ws) {
        ws.close()
      }
    }
  }, [isPlaying, callId])

  return (
    <div className="w-full flex items-center justify-center">
      {!isCallEnded
        ? (
            <Tooltip
              content={org?.preferences?.live_listen_enabled ? (isPlaying ? 'Pause' : 'Listen') : 'Enable'}
              color="white"
              placement="top"
              offset={20}
              className="rounded-md"
            >
              <button
                type="button"
                onClick={() => {
                  if (!org?.preferences?.live_listen_enabled) {
                    navigate('/dashboard/settings?view=RecordingSettings')
                    return
                  }
                  setIsPlaying(!isPlaying)
                }}
                className="w-full aspect-square flex items-center justify-center hover:bg-slate-50/50 transition-colors rounded-md"
              >
                {!org?.preferences?.live_listen_enabled
                  ? (
                      <div className="w-full aspect-square flex items-center justify-center hover:bg-slate-50/50 transition-colors rounded-md">
                        <Lock size={15} />
                      </div>
                    )
                  : !isPlaying
                    ? (
                        <Play size={15} />
                      )
                    : (
                        <SmallSquareAudioWave isPlaying={isPlaying} />
                      )}

              </button>
            </Tooltip>
          )
        : (
            <button
              type="button"
              disabled
              className="w-full aspect-square flex items-center justify-center hover:bg-slate-50/50 transition-colors rounded-md"
            >
              <SmallSquareAudioWave isPlaying={false} />
            </button>
          )}
    </div>
  )
}

function SmallSquareAudioWave({ isPlaying }) {
  const [bars, setBars] = useState([])
  const barCount = 8

  useEffect(() => {
    const initialHeights = Array.from({ length: barCount }, (_, i) => {
      return i % 2 === 0 ? 4 : 2
    })
    setBars(initialHeights)
  }, [])

  function generateRandomHeights(count) {
    return Array.from({ length: count }, () => Math.floor(Math.random() * 8) + 2)
  }

  useEffect(() => {
    let interval

    if (isPlaying) {
      interval = setInterval(() => {
        setBars(generateRandomHeights(barCount))
      }, 100)
    }
    else {
      setBars(Array.from({ length: barCount }, (_, i) => i % 2 === 0 ? 4 : 2))
    }

    return () => clearInterval(interval)
  }, [isPlaying])

  return (
    <div className="flex items-center justify-center w-full">
      <div className="flex items-end space-x-0.5">
        {bars.map((height, index) => (
          <div
            key={index}
            className="w-0.5 rounded-t transition-all duration-75 ease-in-out"
            style={{
              height: `${height}px`,
              backgroundColor: isPlaying ? '#3B82F6' : '#CBD5E1',
              opacity: isPlaying ? 0.7 + (index % 3) * 0.1 : 0.6,
            }}
          />
        ))}
      </div>
    </div>
  )
}
